import {
  API_CONFIG,
  BlogPost,
  BlogsResponse,
  BlogsApiResponse,
  BlogApiResponse,
  Comment,
  CommentsApiResponse,
  CreateCommentRequest,
  CreateCommentApiResponse,
} from '../config/api'

export interface BlogFilters {
  category?: string
  search?: string
  per_page?: number
  query?: string
}

export class BlogService {
  private static instance: BlogService

  private constructor() {}

  public static getInstance(): BlogService {
    if (!BlogService.instance) {
      BlogService.instance = new BlogService()
    }
    return BlogService.instance
  }

  public async getBlogs(page: number = 1, filters: BlogFilters = {}): Promise<BlogsResponse> {
    try {
      const queryParams = new URLSearchParams({
        franchise_id: API_CONFIG.FRANCHISE_ID,
        page: page.toString(),
      })

      // Add per_page parameter
      if (filters.per_page) {
        queryParams.append('per_page', filters.per_page.toString())
      } else {
        queryParams.append('per_page', '12') // Default
      }

      // Category filter
      if (filters.category && filters.category !== 'All') {
        queryParams.append('category', filters.category)
      }

      // Search/query filter
      if (filters.search) {
        queryParams.append('search', filters.search)
      }

      if (filters.query) {
        queryParams.append('query', filters.query)
      }

      const response = await fetch(`${API_CONFIG.BASE_URL}/blogs?${queryParams.toString()}`, {
        headers: {
          'x-api-key': API_CONFIG.API_KEY,
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const apiData: BlogsApiResponse = await response.json()
      return this.transformApiResponse(apiData, page, filters)
    } catch (error) {
      console.error('Error fetching blogs:', error)
      // Return fallback data if API fails
      return this.getFallbackData(page, filters)
    }
  }

  public async getBlogBySlug(slug: string): Promise<BlogPost> {
    try {
      const url = `${API_CONFIG.BASE_URL}/blogs/${slug}?franchise_id=${API_CONFIG.FRANCHISE_ID}`
      console.log('Fetching blog from:', url)

      const response = await fetch(url, {
        headers: {
          'x-api-key': API_CONFIG.API_KEY,
          'Content-Type': 'application/json',
        },
      })

      console.log('Blog API response status:', response.status)

      if (!response.ok) {
        const errorText = await response.text()
        console.error('Blog API error response:', errorText)
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`)
      }

      const apiData: BlogApiResponse = await response.json()
      console.log('Blog API response data:', apiData)

      if (!apiData.blog) {
        throw new Error('No blog data found in response')
      }

      return this.transformBlogPost(apiData.blog)
    } catch (error) {
      console.error('Error fetching blog post:', error)
      // Return fallback data if API fails
      return this.getFallbackBlogPost(slug)
    }
  }

  public async getCommentsBySlug(slug: string): Promise<Comment[]> {
    try {
      const url = `${API_CONFIG.BASE_URL}/blogs/${slug}/comments?franchise_id=${API_CONFIG.FRANCHISE_ID}`
      console.log('Fetching comments from:', url)

      const response = await fetch(url, {
        headers: {
          'x-api-key': API_CONFIG.API_KEY,
          'Content-Type': 'application/json',
        },
      })

      console.log('Comments API response status:', response.status)

      if (!response.ok) {
        const errorText = await response.text()
        console.error('Comments API error response:', errorText)
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`)
      }

      const apiData: CommentsApiResponse = await response.json()
      console.log('Comments API response data:', apiData)

      return apiData.comments || []
    } catch (error) {
      console.error('Error fetching comments:', error)
      return []
    }
  }

  public async createComment(slug: string, comment: CreateCommentRequest): Promise<Comment | null> {
    try {
      const url = `${API_CONFIG.BASE_URL}/blogs/${slug}/comments?franchise_id=${API_CONFIG.FRANCHISE_ID}`
      console.log('Creating comment at:', url)

      const formData = new FormData()
      formData.append('comment[name]', comment.name)
      formData.append('comment[email]', comment.email)
      formData.append('comment[body]', comment.body)

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'x-api-key': API_CONFIG.API_KEY,
        },
        body: formData,
      })

      console.log('Create comment API response status:', response.status)

      if (!response.ok) {
        const errorText = await response.text()
        console.error('Create comment API error response:', errorText)
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`)
      }

      const commentData: CreateCommentApiResponse = await response.json()
      console.log('Create comment API response data:', commentData)

      return commentData
    } catch (error) {
      console.error('Error creating comment:', error)
      return null
    }
  }

  private transformApiResponse(
    apiData: BlogsApiResponse,
    page: number,
    filters: BlogFilters
  ): BlogsResponse {
    const blogs = apiData.blogs?.map((blog) => this.transformBlogPost(blog)) || []

    // Calculate pagination
    const totalCount = apiData.meta?.total_entries || blogs.length
    const perPage = apiData.meta?.per_page || filters.per_page || 12
    const totalPages = apiData.meta?.total_pages || Math.ceil(totalCount / perPage)
    const currentPage = apiData.meta?.current_page || page

    return {
      blogs,
      pagination: {
        current_page: currentPage,
        total_pages: totalPages,
        total_count: totalCount,
        per_page: perPage,
        has_next: currentPage < totalPages,
        has_prev: currentPage > 1,
      },
    }
  }

  private transformBlogPost(apiBlog: BlogPost): BlogPost {
    return {
      ...apiBlog,
      // Add any transformations needed here
      excerpt: apiBlog.excerpt || this.generateExcerpt(apiBlog.description),
      author_name: apiBlog.author_name || 'Travel Team',
      category:
        apiBlog.category || this.determineCategoryFromContent(apiBlog.title, apiBlog.description),
      tags: Array.isArray(apiBlog.tags)
        ? apiBlog.tags
        : this.extractTagsFromContent(apiBlog.title, apiBlog.description),
    }
  }

  private generateExcerpt(content: string, maxLength: number = 150): string {
    if (!content) return ''
    const text = content.replace(/<[^>]*>/g, '') // Remove HTML tags
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text
  }

  private determineCategoryFromContent(title: string, description: string): string {
    const content = `${title} ${description}`.toLowerCase()

    if (
      content.includes('romantic') ||
      content.includes('honeymoon') ||
      content.includes('couple')
    ) {
      return 'Romance'
    }
    if (
      content.includes('adventure') ||
      content.includes('diving') ||
      content.includes('surfing')
    ) {
      return 'Adventure'
    }
    if (content.includes('family') || content.includes('kids') || content.includes('children')) {
      return 'Family'
    }
    if (content.includes('luxury') || content.includes('spa') || content.includes('wellness')) {
      return 'Luxury'
    }
    if (
      content.includes('destination') ||
      content.includes('travel') ||
      content.includes('visit')
    ) {
      return 'Destinations'
    }
    if (content.includes('tip') || content.includes('guide') || content.includes('how to')) {
      return 'Tips'
    }

    return 'Travel'
  }

  private extractTagsFromContent(title: string, description: string): string[] {
    const content = `${title} ${description}`.toLowerCase()
    const tags: string[] = []

    // Common travel destinations
    const destinations = [
      'maldives',
      'mauritius',
      'dubai',
      'seychelles',
      'singapore',
      'thailand',
      'bali',
    ]
    destinations.forEach((dest) => {
      if (content.includes(dest)) tags.push(dest)
    })

    // Common travel themes
    const themes = [
      'luxury',
      'adventure',
      'romantic',
      'family',
      'beach',
      'spa',
      'diving',
      'surfing',
    ]
    themes.forEach((theme) => {
      if (content.includes(theme)) tags.push(theme)
    })

    return tags.slice(0, 5) // Limit to 5 tags
  }

  private getFallbackBlogPost(slug: string): BlogPost {
    return {
      id: 1,
      title: 'Blog Post Not Found',
      description: 'This blog post could not be loaded at the moment.',
      slug,
      status: 'publish',
      blog_type: 'fallback',
      franchise_id: 0,
      user_id: 0,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      featured_image_file_name: null,
      featured_image_content_type: null,
      featured_image_file_size: null,
      featured_image_updated_at: null,
      featured_image: {
        url: 'https://images.unsplash.com/photo-1488646953014-85cb44e25828?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
        thumb_url:
          'https://images.unsplash.com/photo-1488646953014-85cb44e25828?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
        medium_url:
          'https://images.unsplash.com/photo-1488646953014-85cb44e25828?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      },
      excerpt: 'This blog post could not be loaded at the moment.',
      author_name: 'Travel Team',
      category: 'Travel',
      tags: [],
    }
  }

  private getFallbackData(page: number, filters: BlogFilters): BlogsResponse {
    const fallbackBlogs: BlogPost[] = [
      {
        id: 1,
        title: 'Top 10 Destinations for 2025',
        description:
          'Discover the most exciting travel destinations that should be on your bucket list this year.',
        slug: 'top-10-destinations-2025',
        status: 'publish',
        blog_type: 'fallback',
        franchise_id: 0,
        user_id: 0,
        created_at: '2024-01-15T00:00:00Z',
        updated_at: '2024-01-15T00:00:00Z',
        featured_image_file_name: null,
        featured_image_content_type: null,
        featured_image_file_size: null,
        featured_image_updated_at: null,
        featured_image: {
          url: 'https://images.unsplash.com/photo-1488646953014-85cb44e25828?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
          thumb_url:
            'https://images.unsplash.com/photo-1488646953014-85cb44e25828?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
          medium_url:
            'https://images.unsplash.com/photo-1488646953014-85cb44e25828?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
        },
        excerpt:
          'Discover the most exciting travel destinations that should be on your bucket list this year.',
        author_name: 'Travel Team',
        category: 'Destinations',
        tags: ['destinations', 'travel', '2025'],
      },
      {
        id: 2,
        title: 'Travel Tips for First-Time International Travelers',
        description:
          'Essential advice and tips to make your first international trip smooth and memorable.',
        slug: 'travel-tips-first-time-international',
        status: 'publish',
        blog_type: 'fallback',
        franchise_id: 0,
        user_id: 0,
        created_at: '2024-01-10T00:00:00Z',
        updated_at: '2024-01-10T00:00:00Z',
        featured_image_file_name: null,
        featured_image_content_type: null,
        featured_image_file_size: null,
        featured_image_updated_at: null,
        featured_image: {
          url: 'https://images.unsplash.com/photo-1488646953014-85cb44e25828?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
          thumb_url:
            'https://images.unsplash.com/photo-1488646953014-85cb44e25828?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
          medium_url:
            'https://images.unsplash.com/photo-1488646953014-85cb44e25828?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
        },
        excerpt:
          'Essential advice and tips to make your first international trip smooth and memorable.',
        author_name: 'Travel Team',
        category: 'Tips',
        tags: ['tips', 'international', 'travel'],
      },
      {
        id: 3,
        title: 'Best Time to Visit Mauritius',
        description:
          'Learn about the perfect seasons and weather conditions for your Mauritius holiday.',
        slug: 'best-time-visit-mauritius',
        status: 'publish',
        blog_type: 'fallback',
        franchise_id: 0,
        user_id: 0,
        created_at: '2024-01-05T00:00:00Z',
        updated_at: '2024-01-05T00:00:00Z',
        featured_image_file_name: null,
        featured_image_content_type: null,
        featured_image_file_size: null,
        featured_image_updated_at: null,
        featured_image: {
          url: 'https://images.unsplash.com/photo-1488646953014-85cb44e25828?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
          thumb_url:
            'https://images.unsplash.com/photo-1488646953014-85cb44e25828?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
          medium_url:
            'https://images.unsplash.com/photo-1488646953014-85cb44e25828?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
        },
        excerpt:
          'Learn about the perfect seasons and weather conditions for your Mauritius holiday.',
        author_name: 'Travel Team',
        category: 'Destinations',
        tags: ['mauritius', 'weather', 'travel'],
      },
    ]

    const perPage = filters.per_page || 12
    const totalPages = Math.ceil(fallbackBlogs.length / perPage)

    return {
      blogs: fallbackBlogs,
      pagination: {
        current_page: page,
        total_pages: totalPages,
        total_count: fallbackBlogs.length,
        per_page: perPage,
        has_next: page < totalPages,
        has_prev: page > 1,
      },
    }
  }
}
